import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Logo from "../components/Logo";
import PremiumDMCCard from "../components/PremiumDMCCard";
import HowItWorks from "../components/HowItWorks";
import { api } from "../lib/api";
import {
  ArrowRight, Globe2, Zap, ShieldCheck, Sparkles, Crown,
  TrendingUp, Award, Layers, BadgeDollarSign, Users2, MessageSquareQuote,
  Phone, Mail, MapPin, Briefcase, Building2, Rocket, Gift, Headphones, Globe,
  Trophy, Bell, Gauge, BarChart3,
} from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/16116150/pexels-photo-16116150.jpeg";
const HERO_IMG_2 = "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=2000";
const HERO_IMG_3 = "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=2000";
const TEAM_IMG = "https://images.pexels.com/photos/7869132/pexels-photo-7869132.jpeg";

const STATS_LABELS = [
  { key: "dmcs", label: "Verified DMCs", suffix: "+" },
  { key: "agents", label: "Travel Agents", suffix: "+" },
  { key: "countries", label: "Countries", suffix: "+" },
  { key: "avg_response_min", label: "Avg. Response", suffix: " min" },
];

const HOW = [
  { n: "01", t: "Agent posts a lead", d: "Travel agents drop a brief — destination, pax, budget, MICE specifics. AI auto-tags & summarises." },
  { n: "02", t: "Matched DMCs bid", d: "Premium DMCs receive only the leads they can serve. Bids land in minutes, not days." },
  { n: "03", t: "AI evaluates bids", d: "Claude-powered scoring on price, speed, fit and trust. Agents pick the leader with confidence." },
  { n: "04", t: "10% Escrow secures it", d: "Booking confirmed — TrawellB2B holds 10% in escrow until the trip is delivered. Trust, by default." },
];

const TRACTION = [
  "Trawell DMC · Dubai", "Singapore", "Bangkok", "Kuala Lumpur",
  "Hong Kong", "Manila", "Hanoi", "Trawell DMC",
  "140,000+ Travellers", "500+ MICE Groups", "24/7 Concierge", "Est. 2005",
];

export default function LandingPage() {
  const [stats, setStats] = useState({ dmcs: 48, agents: 230, countries: 42, avg_response_min: 7 });
  const [dmcs, setDmcs] = useState([]);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    api.get("/public/stats").then((r) => setStats((s) => ({ ...s, ...r.data }))).catch(() => {});
    api.get("/dmcs?premium_only=true").then((r) => setDmcs(r.data || [])).catch(() => {});
    api.get("/leads/public").then((r) => setLeads(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="App" data-testid="landing-page">
      <Navbar />

      {/* WALKTHROUGH VIDEO — brand-first hook, autoplays muted */}
      <section className="bg-[#0B0B0B] pt-8 pb-14 border-b border-[#1a1a1a] hero-dark" data-testid="walkthrough-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand">Walkthrough · 41 sec</p>
              <h2 className="font-display text-2xl md:text-4xl font-black tracking-tight text-white mt-2">
                Meet the <span className="text-brand">TrawellB2B</span> Procurement Exchange
              </h2>
            </div>
            <p className="text-sm text-white/60 max-w-md">
              A 41-second graphic tour of the buyer flow, DMC bidding, MICE briefs, and Ops verification. Sound on for narration.
            </p>
          </div>
          <div className="relative rounded-md overflow-hidden border border-[#262626] shadow-2xl bg-black" data-testid="walkthrough-frame">
            <video
              controls
              autoPlay
              muted
              playsInline
              loop
              preload="metadata"
              poster="/trawell-logo.png"
              className="w-full h-auto"
              data-testid="walkthrough-video"
            >
              <source src="/media/walkthrough.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* HERO — enrollment focus (kept dark by design for high-contrast) */}
      <section className="relative overflow-hidden border-b border-hairline hero-dark">
        <div className="absolute inset-0">
          <img src={HERO_IMG_2} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />
          <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-luminosity" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B0B0B] via-[#0B0B0B]/85 to-[#0B0B0B]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-transparent to-transparent" />
        <div className="absolute top-1/4 -right-32 w-[480px] h-[480px] bg-brand/25 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 fade-up">
            {/* Big hero logo lockup */}
            <div className="mb-8" data-testid="hero-logo-lockup">
              <Logo size="xl" variant="light" showTagline={false} />
              <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.32em] text-brand">
                Curating Amazing · MICE Procurement Exchange
              </p>
            </div>
            <p className="overline mb-6 flex items-center gap-2 flex-wrap">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Beta launch · Enrollment open · By Trawell DMC</span>
              <Link to="/parent" className="text-muted hover:text-ink normal-case ml-2 underline underline-offset-4">
                See parent company
              </Link>
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter uppercase text-white leading-[0.9] text-balance">
              The world's <span className="text-brand">smartest</span><br />
              MICE procurement<br />exchange.
            </h1>
            <p className="mt-7 text-lg md:text-xl text-[#d4d4d4] max-w-2xl leading-relaxed">
              Post one brief. Get apples-to-apples bids from <span className="text-white font-semibold">Top-10 verified DMCs</span>{" "}
              and <span className="text-brand font-semibold">MICE operators</span> in minutes.
              Escrow-secured. AI-audited. <span className="text-white">Free to join.</span>
            </p>

            {/* Triple enrollment CTAs */}
            <div className="grid sm:grid-cols-3 gap-3 mt-9 max-w-3xl" data-testid="hero-triple-cta">
              <Link
                to="/register"
                state={{ role: "agent" }}
                className="group bg-[#0a0a0a] border border-[#262626] hover:border-brand hover:bg-brand/5 rounded-md p-5 transition-all"
                data-testid="hero-cta-agent"
              >
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-brand" />
                  <p className="overline">Travel agents</p>
                </div>
                <p className="font-display text-lg font-bold text-white mt-3">Post an RFP</p>
                <p className="text-xs text-[#a3a3a3] mt-1">Get bids in minutes.</p>
                <span className="inline-flex items-center gap-1.5 text-brand text-xs font-mono mt-4 uppercase tracking-widest group-hover:gap-2.5 transition-all">
                  Join <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
              <Link
                to="/register"
                state={{ role: "dmc" }}
                className="group bg-[#0a0a0a] border border-[#262626] hover:border-brand hover:bg-brand/5 rounded-md p-5 transition-all"
                data-testid="hero-cta-dmc"
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-brand" />
                  <p className="overline">DMC partners</p>
                </div>
                <p className="font-display text-lg font-bold text-white mt-3">Apply as DMC</p>
                <p className="text-xs text-[#a3a3a3] mt-1">Bid to win.</p>
                <span className="inline-flex items-center gap-1.5 text-brand text-xs font-mono mt-4 uppercase tracking-widest group-hover:gap-2.5 transition-all">
                  Apply <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
              <Link
                to="/register"
                state={{ role: "mice_operator" }}
                className="group bg-[#0a0a0a] border border-[#262626] hover:border-brand hover:bg-brand/5 rounded-md p-5 transition-all"
                data-testid="hero-cta-mice"
              >
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-brand" />
                  <p className="overline">MICE operators</p>
                </div>
                <p className="font-display text-lg font-bold text-white mt-3">Apply as MICE Op</p>
                <p className="text-xs text-[#a3a3a3] mt-1">Win group RFPs.</p>
                <span className="inline-flex items-center gap-1.5 text-brand text-xs font-mono mt-4 uppercase tracking-widest group-hover:gap-2.5 transition-all">
                  Apply <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            </div>

            <div className="flex items-center gap-2 mt-6 text-sm text-[#737373]">
              <span>Already a member?</span>
              <Link to="/login" className="text-white hover:text-brand underline underline-offset-4 decoration-[#404040]" data-testid="hero-login-link">
                Sign in →
              </Link>
            </div>
          </div>

          {/* Live platform card */}
          <div className="md:col-span-5 fade-up delay-200" data-testid="hero-stats-card">
            <div className="bg-black/70 backdrop-blur-md border border-[#262626] rounded-xl p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <p className="overline">Live Platform</p>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono text-[#a3a3a3]">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" /> ACTIVE
                </span>
              </div>
              <div className="grid grid-cols-2 gap-5">
                {STATS_LABELS.map((s) => (
                  <div key={s.key}>
                    <p className="font-display text-3xl font-black text-white">
                      {stats[s.key] ?? "—"}
                      <span className="text-brand">{s.suffix}</span>
                    </p>
                    <p className="text-xs text-[#737373] uppercase tracking-widest mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-[#1a1a1a]">
                <p className="overline mb-3">Recent leads</p>
                <ul className="space-y-2 text-xs">
                  {leads.slice(0, 4).map((l) => (
                    <li key={l.lead_id} className="flex items-center justify-between text-[#a3a3a3]">
                      <span className="truncate pr-3">{l.title}</span>
                      <span className="font-mono text-brand whitespace-nowrap">{l.bid_count}b</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-b border-[#1a1a1a] py-6 overflow-hidden bg-[#0a0a0a]">
        <div className="marquee gap-12">
          {[...TRACTION, ...TRACTION].map((t, i) => (
            <span key={i} className="font-display font-bold text-2xl text-white/40 whitespace-nowrap px-6">
              {t} <span className="text-brand">·</span>
            </span>
          ))}
        </div>
      </section>

      {/* COMPACT PARENT TEASER */}
      <section className="border-b border-[#1a1a1a] bg-[#0a0a0a]" data-testid="parent-teaser">
        <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <p className="overline">Backed by Trawell DMC</p>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white mt-2 leading-tight">
              <span className="text-brand">20 years</span> of DMC craftsmanship · 140K+ travellers · 7 destinations.
            </h2>
          </div>
          <div className="md:col-span-3 grid grid-cols-3 gap-3 text-center">
            <div><p className="font-mono text-brand text-lg font-bold">20+</p><p className="text-[10px] font-mono uppercase tracking-widest text-[#737373]">Years</p></div>
            <div><p className="font-mono text-brand text-lg font-bold">140K+</p><p className="text-[10px] font-mono uppercase tracking-widest text-[#737373]">Pax</p></div>
            <div><p className="font-mono text-brand text-lg font-bold">07</p><p className="text-[10px] font-mono uppercase tracking-widest text-[#737373]">Cities</p></div>
          </div>
          <div className="md:col-span-2 text-right">
            <Link to="/parent" className="btn-ghost text-sm" data-testid="parent-teaser-cta">
              Parent company <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY JOIN — Product pillars */}
      <section id="pillars" className="max-w-7xl mx-auto px-6 py-24" data-testid="pillars-section">
        <div className="grid md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-6">
            <p className="overline">Why join TrawellB2B</p>
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tighter text-white mt-4 uppercase leading-[0.95]">
              Built for travel<br />
              that <span className="text-brand">closes faster.</span>
            </h2>
          </div>
          <div className="md:col-span-6 text-[#a3a3a3] leading-relaxed text-lg">
            <p>
              Whether you sell trips or operate them — TrawellB2B compresses sourcing
              from days to minutes. AI-matched, escrow-secured, premium-curated.
            </p>
            <p className="mt-3 text-white font-semibold">Free to join. Pay only when a booking confirms.</p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {[
            { i: <Globe className="w-6 h-6" />, t: "50+ Countries", d: "Verified DMCs from Dubai to Tokyo, Reykjavik to Cape Town. Source globally, deliver locally." },
            { i: <Zap className="w-6 h-6" />, t: "Bids in minutes", d: "Lead → matched DMC → first qualified bid in under 10 minutes. Real-time SSE notifications." },
            { i: <Trophy className="w-6 h-6" />, t: "MICE specialists", d: "From 40-pax offsites to 2,500-pax dealer conferences. Plan with the best in the world." },
            { i: <Sparkles className="w-6 h-6" />, t: "AI bid scoring", d: "Claude Sonnet evaluates price, speed, fit and trust. The right pick — surfaced." },
            { i: <ShieldCheck className="w-6 h-6" />, t: "10% Escrow security", d: "We hold the deposit until service delivery. Trust, by default. No DIY chasing." },
            { i: <Crown className="w-6 h-6" />, t: "Premium placement", d: "Verified DMCs get priority. Travel agents get the best — every time, by design." },
            { i: <Headphones className="w-6 h-6" />, t: "24/7 concierge", d: "Trawell DMC's two-decade ops desk backs every booking. Crisis-proof operations." },
            { i: <Gauge className="w-6 h-6" />, t: "One dashboard", d: "Leads, bids, MICE quotes, escrow, AI insights — all role-aware, all in one place." },
            { i: <Gift className="w-6 h-6" />, t: "Free to join", d: "Zero setup fees. No monthly minimums. Pay only commission on confirmed bookings." },
          ].map((p, i) => (
            <div
              key={p.t}
              className="bg-[#0a0a0a] border border-[#262626] hover:border-brand transition-colors rounded-xl p-6 fade-up"
              style={{ animationDelay: `${i * 0.05}s` }}
              data-testid={`pillar-${p.t.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className="inline-flex items-center justify-center w-12 h-12 bg-brand/10 text-brand rounded-md">{p.i}</span>
              <h3 className="font-display font-bold text-white text-xl mt-4">{p.t}</h3>
              <p className="text-sm text-[#a3a3a3] mt-2 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TWIN ENROLLMENT — DMC vs AGENT */}
      <section className="border-y border-[#1a1a1a] bg-gradient-to-b from-black to-[#080808] py-24" data-testid="twin-enrollment">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="overline">Two ways to win</p>
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tighter text-white mt-4 uppercase leading-[0.95]">
              Pick your <span className="text-brand">side</span>.<br />
              Start in <span className="text-brand">90 seconds.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* AGENT */}
            <div className="relative bg-[#0a0a0a] border border-[#262626] rounded-2xl p-8 md:p-10 overflow-hidden group hover:border-brand transition-colors">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-brand/10 rounded-full blur-3xl group-hover:bg-brand/20 transition-colors" />
              <div className="relative">
                <Briefcase className="w-10 h-10 text-brand" />
                <p className="overline mt-5">For travel agents</p>
                <h3 className="font-display text-3xl md:text-4xl font-bold text-white mt-3 leading-tight">
                  Source like a power buyer.
                </h3>
                <ul className="mt-6 space-y-3 text-sm text-[#d4d4d4]">
                  {[
                    "Post one brief → reach 50+ verified DMCs",
                    "AI-matched DMCs only — no spam, no junk bids",
                    "Real-time bid leaderboard with AI fit-scores",
                    "MICE-grade templates for 40 → 2,500 pax",
                    "10% escrow protects every booking",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 mt-2 bg-brand rounded-full flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="grid grid-cols-3 gap-2 mt-7 pt-6 border-t border-[#1a1a1a]">
                  <div><p className="font-mono text-brand text-xl font-bold">7m</p><p className="text-[10px] font-mono uppercase tracking-widest text-[#737373]">Avg first bid</p></div>
                  <div><p className="font-mono text-brand text-xl font-bold">$0</p><p className="text-[10px] font-mono uppercase tracking-widest text-[#737373]">Joining fee</p></div>
                  <div><p className="font-mono text-brand text-xl font-bold">5★</p><p className="text-[10px] font-mono uppercase tracking-widest text-[#737373]">Verified DMCs</p></div>
                </div>
                <Link to="/register" state={{ role: "agent" }} className="btn-primary w-full justify-center mt-7" data-testid="enroll-agent-cta">
                  Join as Travel Agent <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* DMC */}
            <div className="relative bg-[#0a0a0a] border border-[#262626] rounded-2xl p-8 md:p-10 overflow-hidden group hover:border-brand transition-colors">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-brand/10 rounded-full blur-3xl group-hover:bg-brand/20 transition-colors" />
              <div className="relative">
                <Building2 className="w-10 h-10 text-brand" />
                <p className="overline mt-5">For DMC partners</p>
                <h3 className="font-display text-3xl md:text-4xl font-bold text-white mt-3 leading-tight">
                  Win bookings, on autopilot.
                </h3>
                <ul className="mt-6 space-y-3 text-sm text-[#d4d4d4]">
                  {[
                    "Receive only the leads you can serve",
                    "Bid fast → win more · Premium tier = priority placement",
                    "Showcase your specialties (MICE, leisure, honeymoons)",
                    "Get paid via 10% escrow — no chasing, no defaults",
                    "Real-time agent network · 200+ active travel agents",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 mt-2 bg-brand rounded-full flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="grid grid-cols-3 gap-2 mt-7 pt-6 border-t border-[#1a1a1a]">
                  <div><p className="font-mono text-brand text-xl font-bold">230+</p><p className="text-[10px] font-mono uppercase tracking-widest text-[#737373]">Agents</p></div>
                  <div><p className="font-mono text-brand text-xl font-bold">10%</p><p className="text-[10px] font-mono uppercase tracking-widest text-[#737373]">Escrow held</p></div>
                  <div><p className="font-mono text-brand text-xl font-bold">24/7</p><p className="text-[10px] font-mono uppercase tracking-widest text-[#737373]">Lead flow</p></div>
                </div>
                <Link to="/register" state={{ role: "dmc" }} className="btn-primary w-full justify-center mt-7" data-testid="enroll-dmc-cta">
                  Apply as DMC <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>

          <p className="text-center text-sm text-[#737373] mt-8">
            <span className="text-[#a3a3a3]">Free to join.</span> No credit card. Approval in under 24 hours.
          </p>
        </div>
      </section>

      {/* VALUE PROP */}
      <section id="stats" className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <p className="overline">Why TrawellB2B</p>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight text-white mt-4 leading-[1.05]">
              Sourcing trips at the<br />
              speed of <span className="text-brand">software.</span>
            </h2>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { i: <Zap className="w-5 h-5" />, t: "7-min avg first bid", d: "Lead → matched DMC → first qualified bid in under 10 minutes." },
              { i: <ShieldCheck className="w-5 h-5" />, t: "10% Escrow on every booking", d: "We hold the deposit. DMCs deliver. Funds release on confirmation." },
              { i: <Sparkles className="w-5 h-5" />, t: "AI bid scoring", d: "Claude evaluates price, speed, fit, premium-trust. The right pick, surfaced." },
              { i: <Crown className="w-5 h-5" />, t: "Premium listings", d: "Verified DMCs get priority — the network's most trusted, by design." },
            ].map((c, i) => (
              <div key={i} className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-6 hover:border-brand transition-colors">
                <span className="inline-flex items-center justify-center w-10 h-10 bg-brand/10 text-brand rounded-md">{c.i}</span>
                <h3 className="font-display font-bold text-white text-xl mt-4">{c.t}</h3>
                <p className="text-sm text-[#a3a3a3] mt-2 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 3-tab explainer (For Agents / DMCs / MICE Operators) */}
      <HowItWorks />

      {/* PREMIUM DMCs */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="overline">Premium Showcase</p>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight text-white mt-4">
              Verified DMCs, <span className="text-brand">globally curated.</span>
            </h2>
          </div>
          <Link to="/dmcs" className="btn-ghost text-sm">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {(dmcs.length ? dmcs : Array(6).fill({ user_id: "", name: "Loading", country: "—", premium: true })).slice(0, 6).map((d, i) => (
            <PremiumDMCCard key={d.user_id || i} dmc={d} idx={i} />
          ))}
        </div>
      </section>

      {/* MICE / Escrow split */}
      <section className="border-t border-[#1a1a1a] bg-[#080808] py-24">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-6 bg-[#050505] border border-[#262626] rounded-xl p-8 md:p-12">
            <p className="overline">MICE Quotes</p>
            <h3 className="font-display text-3xl font-bold text-white mt-3">Built for Meetings, Incentives, Conferences & Events.</h3>
            <p className="text-sm text-[#a3a3a3] mt-3 leading-relaxed">
              Volume queries, ballroom specs, F&B counts, AV — TrawellDMC handles the messy detail of MICE
              with structured templates and competitive bidding from event-grade DMCs.
            </p>
            <div className="grid grid-cols-3 gap-3 mt-7">
              <div className="bg-black border-l-4 border-brand p-4"><p className="overline">Pax</p><p className="font-mono text-white text-lg mt-1">250</p></div>
              <div className="bg-black border-l-4 border-[#262626] p-4"><p className="overline">Days</p><p className="font-mono text-white text-lg mt-1">4</p></div>
              <div className="bg-black border-l-4 border-[#262626] p-4"><p className="overline">Budget</p><p className="font-mono text-white text-lg mt-1">$240k</p></div>
            </div>
          </div>
          <div className="md:col-span-6 bg-[#050505] border border-[#262626] rounded-xl p-8 md:p-12 relative overflow-hidden">
            <p className="overline">10% Escrow</p>
            <h3 className="font-display text-3xl font-bold text-white mt-3">Trust, engineered into every transaction.</h3>
            <p className="text-sm text-[#a3a3a3] mt-3 leading-relaxed">
              Travel agents reserve confidence. DMCs receive guaranteed-intent leads. We hold a 10% deposit
              in escrow — released only after service delivery confirms.
            </p>
            <ol className="relative pl-6 mt-8 space-y-4">
              <span className="absolute left-2 top-2 bottom-2 w-px bg-gradient-to-b from-brand to-[#262626]" />
              {["Bid Accepted", "10% Held", "Service Delivered", "Funds Released"].map((s, i) => (
                <li key={s} className="relative">
                  <span className={`absolute -left-6 top-0.5 w-4 h-4 rounded-full border-2 ${i < 2 ? "bg-brand border-brand" : "bg-black border-[#262626]"}`} />
                  <p className="text-sm text-white font-medium">{s}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5 relative h-[420px] rounded-xl overflow-hidden border border-[#262626]">
          <img src={TEAM_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        </div>
        <div className="md:col-span-7">
          <MessageSquareQuote className="w-10 h-10 text-brand" />
          <blockquote className="font-display text-2xl md:text-3xl text-white leading-snug mt-5 max-w-2xl">
            "TrawellDMC turned our 3-day quote scramble into a 30-minute decision. The AI bid scores
            are eerily accurate — and the escrow gives our customers peace of mind."
          </blockquote>
          <p className="mt-6 overline">Aria · Demo Voyages, Singapore</p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="bg-gradient-to-br from-[#1a1a1a] to-black border border-[#262626] rounded-2xl p-12 md:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-30 bg-gradient-to-tr from-brand/20 via-transparent to-transparent" />
          <p className="overline relative">Curating amazing</p>
          <h2 className="relative font-display text-4xl md:text-6xl font-black tracking-tighter text-white mt-4 uppercase">
            Pitch us. <span className="text-brand">We pitch back.</span>
          </h2>
          <p className="relative text-[#a3a3a3] mt-5 max-w-2xl mx-auto">
            100 active DMCs · 200 travel agents · 42 countries. Your next booking is already in the network.
          </p>
          <div className="relative flex flex-wrap items-center justify-center gap-4 mt-9">
            <Link to="/register" className="btn-primary" data-testid="cta-bottom-register">
              Become an Agent <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/register" className="btn-ghost" data-testid="cta-bottom-dmc">
              Apply as a DMC
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#1a1a1a] bg-black">
        <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="font-display font-black text-2xl text-white">TRAWELL<span className="text-brand">B2B</span></p>
            <p className="text-xs text-[#737373] mt-1 font-mono uppercase tracking-widest">By Trawell DMC · Curating Amazing</p>
            <p className="text-sm text-[#a3a3a3] mt-5 max-w-md leading-relaxed">
              The B2B partner-sourcing network powered by Trawell DMC — Dubai's premium destination
              management operator since 2005. Seven destinations across South-East Asia & West Asia.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-5">
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#737373] border border-[#262626] px-2 py-1">2 Decades</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#737373] border border-[#262626] px-2 py-1">7 Destinations</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#737373] border border-[#262626] px-2 py-1">24/7 Concierge</span>
            </div>
          </div>
          <div className="md:col-span-3">
            <p className="overline">Platform</p>
            <ul className="mt-4 space-y-2 text-sm text-[#a3a3a3]">
              <li><Link to="/dmcs" className="hover:text-white">Premium DMCs</Link></li>
              <li><a href="#how" className="hover:text-white">How it works</a></li>
              <li><a href="#parent" className="hover:text-white">Parent Company</a></li>
              <li><Link to="/register" className="hover:text-white">Join the network</Link></li>
            </ul>
          </div>
          <div className="md:col-span-4">
            <p className="overline">Trawell DMC HQ</p>
            <ul className="mt-4 space-y-3 text-sm text-[#a3a3a3]">
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" /> Al Barsha, Dubai, UAE</li>
              <li className="flex items-start gap-2"><Phone className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" /><span className="font-mono text-xs">+971 52 452 6313<br />+971 58 179 9090<br />+91 77359 19191</span></li>
              <li className="flex items-start gap-2"><Mail className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" /><a href="mailto:hello@trawelldmc.com" className="hover:text-white">hello@trawelldmc.com</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#1a1a1a] py-5">
          <p className="max-w-7xl mx-auto px-6 text-xs text-[#737373] flex flex-col md:flex-row gap-2 md:items-center md:justify-between">
            <span>© {new Date().getFullYear()} Trawell DMC · TrawellB2B Network</span>
            <span className="font-mono uppercase tracking-widest">Dubai · Singapore · Bangkok · KL · HK · Manila · Hanoi</span>
          </p>
        </div>
      </footer>
    </div>
  );
}
